import React, { useMemo, useRef } from "react";
import JsPDF from "jspdf";
import html2canvas from "html2canvas";
import styled from "styled-components";
import { Bar } from "react-chartjs-2";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from "chart.js";
import Chevron from "../../../../../assets/images/icons/Web_Sprite_Icon_Chevron_Grey_analytics.png";
import CrownIcon from "../../../../../assets/images/icons/Web_Sprite_Icon_Analytics_Crown.png";
import TZLogo from "../../../../../assets/images/logos/colorLogo.png";
import HeroLogoIntraverse from "../../../../../assets/images/logos/HeroLogoIntraverse.png";
import { capitalizeFirstLetter, createCrownPlugin } from "../../../../../utils/generalUtils";
import { ReactComponent as ExportIcon } from "../../../../../assets/images/icons/Mediamodifier-Design.svg";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const BarColorPalette = [
    "#BA4BED",
    "#B056F7",
    "#A460FF",
    "#9869FF",
    "#8A72FF",
    "#7B7CFF",
    "#6B85FF",
    "#5D8EFF",
    "#4E96FF",
    "#3E9FFF",
    "#2EA7FF",
    "#1EAFFF",
];

const crownPlugin = {
    id: "crownPlugin",
    afterDatasetsDraw(chart) {
        const { ctx } = chart;
        const firstBar = chart.getDatasetMeta(0).data[0];
        if (firstBar) {
            const img = new Image();
            img.src = CrownIcon;
            const iconSize = 24;
            const x = firstBar.x - iconSize / 2;
            const y = firstBar.y - iconSize - 5;
            ctx.drawImage(img, x, y, iconSize, iconSize);
        }
    },
};

const Barchart = ({
    chartTitle,
    data = [],
    totalVisit = 0,
    Filter,
    ImageContainer,
    displayDate,
    isBarChartModalOpen,
    setIsBarChartModalOpen,
    pdfStartDate,
    pdfEndDate,
    client,
}) => {
    const chartRef = useRef(null);
    const maxItems = isBarChartModalOpen ? data?.length : 7;
    const visibleData = data.slice(0, maxItems);
    const maxLabelLength = 7;
    const shouldRotate = visibleData.length > 5 && visibleData.some((item) => item?.name?.length > maxLabelLength);

    const chartData = {
        labels: visibleData.map((item) => item.name),
        datasets: [
            {
                data: visibleData.map((item) => item.visits),
                backgroundColor: BarColorPalette.slice(0, maxItems),
                borderColor: BarColorPalette.slice(0, maxItems),
                borderWidth: 1,
                barThickness: 35,
                categoryPercentage: 0.3,
                barPercentage: 0.3,
            },
        ],
    };

    const options = useMemo(() => {
        const visitsArray = visibleData.map((item) => Number(item.visits) || 0);
        const maxVisits = Math.max(...visitsArray, 0);

        const magnitude = 10 ** Math.floor(Math.log10(maxVisits));
        let niceMax = Math.ceil(maxVisits / magnitude) * magnitude;

        if (niceMax < 5) niceMax = 5;
        if (niceMax <= 10) niceMax = 10;

        let steps;
        if (visibleData.length <= 5) {
            steps = 4;
        } else if (visibleData.length <= 10) {
            steps = 5;
        } else {
            steps = 6;
        }

        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false,
                },
                title: {
                    display: false,
                },
                tooltip: {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                    titleFont: {
                        family: "Barlow",
                        size: 12,
                        style: "normal",
                        weight: "700",
                    },
                    titleColor: "rgba(255, 255, 255, 0.50)",
                    bodyFont: {
                        family: "Barlow",
                        size: 12,
                        weight: "400",
                    },
                    displayColors: false,
                    callbacks: {
                        beforeBody: (context) => context[0].label,
                        title: () => displayDate,
                        label: (context) => `Visits: ${context.parsed.y.toLocaleString()}`,
                    },
                },
            },
            scales: {
                x: {
                    ticks: {
                        autoSkip: false,
                        color: "#fbfcff",
                        font: { family: "Barlow", size: 12 },
                        maxRotation: shouldRotate ? 45 : 0,
                        minRotation: shouldRotate ? 45 : 0,
                        padding: 10,
                    },
                    grid: { display: false },
                    border: {
                        display: false,
                    },
                },
                y: {
                    beginAtZero: true,
                    max: niceMax,
                    ticks: {
                        color: "#fbfcff",
                        font: { family: "Barlow", size: 12 },
                        stepSize: steps,
                        callback(value) {
                            return value.toLocaleString();
                        },
                    },
                    grid: {
                        color: "rgba(255, 255, 255, 0.1)",
                    },
                    border: {
                        display: false,
                    },
                },
            },
            layout: {
                padding: {
                    left: 20,
                    right: 20,
                    top: 20,
                    bottom: shouldRotate ? 0 : 10,
                },
            },
        };
    }, [displayDate, shouldRotate, isBarChartModalOpen, visibleData]);

    const handleExportPDF = async () => {
        if (!data || data.length === 0) return;

        const pdf = new JsPDF("landscape", "pt", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const margin = 40;
        const chartMaxHeight = 300;
        const paddingBetweenChartAndLegends = 20;
        const itemsPerPage = 20;
        const totalPages = Math.ceil(data.length / itemsPerPage);

        for (let pageIndex = 0; pageIndex < totalPages; pageIndex += 1) {
            // get first page first bar's index only
            const firstIndex = pageIndex * itemsPerPage;
            const start = pageIndex * itemsPerPage;
            const end = start + itemsPerPage;
            const pageData = data.slice(start, end);

            const barCount = pageData.length;

            // ✅ Force canvas size to match PDF drawing area
            const canvasWidth = pageWidth - 2 * margin;
            const offCanvas = document.createElement("canvas");
            offCanvas.width = canvasWidth;
            offCanvas.height = chartMaxHeight;

            // Chart.js uses style size for layout
            offCanvas.style.width = `${canvasWidth}px`;
            offCanvas.style.height = `${chartMaxHeight}px`;
            offCanvas.setAttribute("style", "all: unset; background: #000;");

            const ctx = offCanvas.getContext("2d");

            // Max visits
            const maxVisits = pageData.reduce((max, item) => Math.max(max, Number(item.visits)), 0);

            // Decide number of y steps
            let steps;
            if (pageData.length <= 5) {
                steps = 4;
            } else if (pageData.length <= 10) {
                steps = 5;
            } else {
                steps = 6;
            }

            // Compute nice max with headroom
            const niceMax = Math.ceil((maxVisits * 1.15) / 10) * 10;
            const stepSize = Math.ceil(niceMax / steps / 10) * 10;

            const chartData1 = {
                labels: pageData.map((item) => item.name),
                datasets: [
                    {
                        data: pageData.map((item) => Number(item.visits)),
                        backgroundColor: BarColorPalette.slice(0, pageData.length),
                        borderColor: BarColorPalette.slice(0, pageData.length),
                        borderWidth: 1,
                        barThickness: 20,
                        categoryPercentage: 0.5,
                        barPercentage: 0.5,
                    },
                ],
            };

            const tempChart = new ChartJS(ctx, {
                type: "bar",
                data: chartData1,
                options: {
                    responsive: false,
                    maintainAspectRatio: false,
                    animation: false,
                    plugins: { legend: { display: false }, tooltip: { enabled: false } },
                    scales: {
                        x: {
                            ticks: {
                                autoSkip: false,
                                color: "#fbfcff",
                                font: { family: "Barlow", size: 10 },
                                maxRotation: 45,
                                minRotation: 45,
                                padding: 10,
                            },
                            grid: { display: false },
                            border: { display: false },
                        },
                        y: {
                            beginAtZero: true,
                            max: niceMax,
                            ticks: {
                                color: "#fbfcff",
                                font: { family: "Barlow", size: 10 },
                                stepSize,
                                callback(value) {
                                    return value.toLocaleString();
                                },
                            },
                            grid: { color: "rgba(255, 255, 255, 0.1)" },
                            border: { display: false },
                        },
                    },
                    backgroundColor: "#000000",
                },
                plugins: [
                    createCrownPlugin({
                        pageIndex, // current page
                        allData: data, // full dataset
                        onlyFirstPage: true, // crown only on page 1
                    }),
                ],
            });

            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, 100));
            const chartImage = offCanvas.toDataURL("image/png", 1.0);

            // Black background
            pdf.setFillColor(0, 0, 0);
            pdf.rect(0, 0, pageWidth, pageHeight, "F");

            // ✅ Add watermark
            pdf.saveGraphicsState();
            pdf.setGState(new pdf.GState({ opacity: 0.2 })); // 10% opacity
            const wmWidth = 450;
            const wmHeight = 400;
            const wmX = (pageWidth - wmWidth) / 2; // center horizontally
            const wmY = pageHeight * 0.15;
            pdf.addImage(HeroLogoIntraverse, "PNG", wmX, wmY, wmWidth, wmHeight, "", "NONE", 0);

            pdf.restoreGraphicsState();
            // Header
            const headerY = 30;
            const logoWidth = 70;
            const logoHeight = 70;
            pdf.addImage(TZLogo, "PNG", margin, headerY, logoWidth, logoHeight);
            const spacingBetweenLogos = 15;
            const textStartX = margin + logoWidth + spacingBetweenLogos + 10;

            pdf.setFontSize(14);
            pdf.setTextColor(255, 255, 255);
            pdf.text(`${capitalizeFirstLetter(client)}: ${chartTitle}`, textStartX, headerY + 20, { align: "left" });

            pdf.setFontSize(10);
            pdf.text(`Duration: ${displayDate}`, textStartX, headerY + 40, { align: "left" });
            pdf.text(`(${pdfStartDate} - ${pdfEndDate})`, textStartX, headerY + 55, { align: "left" });

            // Chart below header
            const chartTop = headerY + logoHeight + 30;
            pdf.addImage(chartImage, "PNG", margin, chartTop, canvasWidth, chartMaxHeight);

            // Legends layout
            const footerHeight = 40;
            const legendStartY = chartTop + chartMaxHeight + paddingBetweenChartAndLegends;
            const legendEndY = pageHeight - footerHeight - margin;
            const availableHeightForLegends = legendEndY - legendStartY;

            pdf.setFontSize(9);
            pdf.setTextColor(255, 255, 255);

            const rows = 5; // ✅ fixed number of rows
            const cols = 4; // ✅ fixed number of columns
            const minLineHeight = 18;
            const actualCols = Math.ceil(pageData.length / rows);
            const colWidth = (pageWidth - 2 * margin) / actualCols;

            pageData.forEach((item, index) => {
                const colIndex = Math.floor(index / rows); // fill by row, then move to next column
                const rowIndex = index % rows;

                const xBase = margin + colIndex * colWidth;
                const y = legendStartY + rowIndex * minLineHeight + minLineHeight;

                // ✅ Color swatch
                const swatchSize = 10;
                const swatchX = xBase;
                const swatchY = y - swatchSize + 2;
                pdf.setFillColor(BarColorPalette[index % BarColorPalette.length]);
                pdf.rect(swatchX, swatchY, swatchSize, swatchSize, "F");

                // ✅ Name (normal)
                const nameX = swatchX + swatchSize + 3;
                const label = `${item.name}`;
                pdf.setFont("helvetica", "normal");
                pdf.setTextColor(255, 255, 255);
                pdf.text(label, nameX, y);

                // ✅ Visits (with dotted leader line, normal)
                const value = item.visits.toLocaleString();
                const nameWidth = pdf.getTextWidth(label);
                const valueWidth = pdf.getTextWidth(value);
                const dotsStartX = nameX + nameWidth + 2;
                const valueX = xBase + colWidth - 10;
                const dotsEndX = valueX - valueWidth - 4;

                pdf.setFont("helvetica", "normal");
                pdf.setTextColor(150, 150, 150);
                let dotX = dotsStartX;
                while (dotX < dotsEndX) {
                    pdf.text(".", dotX, y);
                    dotX += 2.5;
                }

                pdf.setTextColor(255, 255, 255);
                pdf.text(value, valueX, y, { align: "right" });
            });

            // Footer page numbers
            pdf.setFontSize(9);
            pdf.setTextColor(180, 180, 180);
            pdf.text(`Page ${pageIndex + 1} of ${totalPages}`, pageWidth / 2, pageHeight - 20, { align: "center" });

            tempChart.destroy();
            if (pageIndex < totalPages - 1) pdf.addPage();
        }
        const filename = `${client.toLowerCase()}_${chartTitle.replace(/\s+/g, "_").toLowerCase()}_${Date.now()}.pdf`;
        pdf.save(filename);
    };

    return (
        <BarchartContainer>
            <BarchartHeader>
                {!isBarChartModalOpen && (
                    <>
                        <BarchartTitle>{chartTitle}</BarchartTitle>
                        {totalVisit > 0 && (
                            <IconWrapper onClick={handleExportPDF}>
                                <StyledExportIcon />
                                <TooltipIcon>Export PDF</TooltipIcon>
                            </IconWrapper>
                        )}
                    </>
                )}

                {isBarChartModalOpen && (
                    <>
                        <PopupDateTitle>{displayDate}</PopupDateTitle>
                        <TotalVisits>
                            Total Visits: <span>{totalVisit.toLocaleString()}</span>
                        </TotalVisits>
                    </>
                )}
            </BarchartHeader>

            <HorizontalScrollContainer>
                <ChartWrapper barCount={visibleData.length} isModal={isBarChartModalOpen}>
                    <Bar ref={chartRef} data={chartData} options={options} plugins={[crownPlugin]} />
                </ChartWrapper>
            </HorizontalScrollContainer>

            {isBarChartModalOpen && (
                <>
                    {visibleData.length > 15 && <ScrollHint>← Scroll to view all bars</ScrollHint>}

                    <LegendContainer>
                        <LegendHeader>
                            <HeaderText>Title</HeaderText>
                            <HeaderText>Visits</HeaderText>
                        </LegendHeader>
                        {data.map((item, index) => (
                            <ListItem key={item.name} isOdd={index % 2 === 0}>
                                <Name rank={index}>{`${index + 1}. ${item.name}`}</Name>
                                <Visits rank={index}>{item.visits.toLocaleString()}</Visits>
                            </ListItem>
                        ))}
                    </LegendContainer>
                </>
            )}
            {!isBarChartModalOpen && (
                <>
                    <ChartFooter>
                        <TotalVisits>
                            Total Visits: <span>{totalVisit.toLocaleString()}</span>
                        </TotalVisits>
                        <ChartDetailsContainer onClick={() => setIsBarChartModalOpen(true)}>
                            <ChartDetailText>View more details</ChartDetailText>
                            <ImageContainer
                                src={Chevron}
                                width={"17px"}
                                height={"17px"}
                                padding={"1.324px 0.294px"}
                                alt="chevron"
                                style={{ rotate: "180deg" }}
                            />
                        </ChartDetailsContainer>
                    </ChartFooter>
                </>
            )}
        </BarchartContainer>
    );
};

export default Barchart;

const BarchartContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;

    @media (max-width: 1440px) {
    }
`;

const BarchartHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex: 1 0 0;

    @media (max-width: 768px) {
        flex-direction: column;
        gap: 10px;
    }
`;

const BarchartTitle = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 20px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    text-transform: capitalize;
`;

const FilterText = styled.p`
    color: var(--White---Black-White, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.5px;

    @media (max-width: 768px) {
        flex: 1;
    }
`;

const ChartWrapper = styled.div`
    position: relative;
    width: 100%;
    height: 300px; /* adjust height for normal view */
    flex-grow: 1;
    min-width: ${({ isModal, barCount }) => (isModal ? `${barCount * 70}px` : "auto")};
    canvas {
        width: 100% !important;
        height: 100% !important;
    }

    @media (max-width: 768px) {
        min-width: ${({ isModal, barCount }) => (isModal ? `${barCount * 40}px` : "auto")};
        height: 220px;
    }
`;

const TotalVisits = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    text-align: right;
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;

    span {
        color: var(--Text-Primary, #fbfcff);
        font-family: "Barlow";
        font-size: 16px;
        font-style: normal;
        font-weight: 600;
        line-height: normal;
    }
`;

const ChartFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
`;

const ChartDetailsContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
`;
const ChartDetailText = styled.div`
    color: var(--Purple-Secondary, #9d75f5);
    text-align: right;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;

    @media (max-width: 768px) {
        font-size: 16px;
    }
`;

const LegendContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    margin-top: 16px;
    max-height: 300px; /* limit height for vertical scrollbar */
    overflow-y: auto; /* vertical scroll when needed */
    overflow-x: hidden;

    /* Custom scrollbar styling for Webkit browsers */
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background-color: rgba(157, 117, 245, 0.5);
        border-radius: 4px;
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: rgba(157, 117, 245, 0.5) transparent;
`;

const ScrollHint = styled.div`
    color: #ced1d9;
    font-size: 14px;
    font-family: "Barlow";
    text-align: center;
    opacity: 0.75;
    user-select: none;
    font-weight: 500;
`;

const LegendHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    min-width: 600px; /* or adjust based on your content */
    white-space: nowrap; /* prevent wrapping */
`;

const HeaderText = styled.div`
    color: var(--Text-Secondary, #ced1d9);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
`;

const ListItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${({ isOdd }) => (isOdd ? "10px 15px" : "8px 15px")};
    background-color: ${({ isOdd }) => (isOdd ? "rgba(255, 255, 255, 0.04)" : "transparent")};
    width: 100%;
    box-sizing: border-box;
`;

const Name = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    min-width: 0;
    text-align: left;
`;

const Visits = styled.div`
    color: var(--Text-Primary, #fbfcff);
    font-family: "Barlow";
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-align: left;
`;

const PopupDateTitle = styled.p`
    color: var(--White---Black-Light-Grey, #9d9d9d);
    leading-trim: both;
    text-edge: cap;
    font-family: "Barlow";
    font-size: 18px;
    font-style: normal;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.8px;
    text-transform: uppercase;
`;

const HorizontalScrollContainer = styled.div`
    width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;

    & > div {
        display: flex;
        min-width: ${({ barCount }) => barCount * 60}px; /* adjust per bar */
    }
`;

const IconWrapper = styled.div`
    position: relative;
    display: inline-block;
    cursor: pointer;
    margin-left: auto;

    &:hover span {
        visibility: visible;
        opacity: 1;
    }
`;

const StyledExportIcon = styled(ExportIcon)`
    width: 40px;
    height: 40px;
`;

const TooltipIcon = styled.span`
    visibility: hidden;
    opacity: 0;
    position: absolute;
    bottom: -30px; /* below the icon */
    left: 50%;
    transform: translateX(-50%);
    background-color: #333;
    color: #fff;
    font-size: 12px;
    font-family: "Barlow", sans-serif;
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
    transition: opacity 0.2s ease;
`;
